import React, {useState, useCallback, useImperativeHandle, forwardRef, useEffect, useRef} from 'react'
import {Box} from '@mui/material'
import {v4 as uuidv4} from 'uuid'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import ContextWindowBar from './ContextWindowBar'
import SiteBuilderOptionsWidget from './SiteBuilderOptionsWidget'
import DiscoveryQuestionWidget from './DiscoveryQuestionWidget'
import ObjectivesWidget from './ObjectivesWidget'
import {useCrosstabsState, type CrosstabsTab} from './useCrosstabsState'
import {useVoiceInput} from './useVoiceInput'
import {usePostHog} from '../../contexts/PostHogProvider'
import {useOptionalUserIdentity} from '../../contexts/UserIdentityContext'
import type {ChatMessage, ChatService, CrosstabsClassificationData, CrosstabsFileRequestData, CrosstabsObjectivesData, CrosstabsOptionsData, CrosstabsPlanData, DiscoveryQuestionData, IntentResult, McpAppData, McpAppInputData, SiteBuilderCodeChunkData, SiteBuilderOptionsData, SiteBuilderPlanData} from './types'

export interface R3ChatHandle {
  reset: () => void
  sendMessage: (text: string) => void
  /** Resume a previous session by loading its messages into the chat */
  loadSession: (sessionId: string, messages: Array<{role: 'user' | 'assistant'; content: string; agent?: string; handoff?: {from: string; to: string}}>) => void
}

interface R3ChatProps {
  chatService: ChatService
  onSessionCreated?: (sessionId: string) => void
  reachyAvatarUrl?: string
  headerActions?: React.ReactNode
  onMessagesChange?: (messages: Array<{role: 'user' | 'assistant'; content: string}>) => void
  onSessionInfoChange?: (info: {intentSummary?: string; startedAt?: number}) => void
  onSiteBuilderPlan?: (data: SiteBuilderPlanData) => void
  onSiteBuilderGenerating?: (siteId: number) => void
  onSiteBuilderCodeChunk?: (data: SiteBuilderCodeChunkData) => void
  onSiteBuilderPreview?: (html: string, siteId: number) => void
  onSiteBuilderPublished?: (url: string, siteId: number) => void
  onStreamingAssistantContent?: (content: string | null) => void
  onCrosstabsClassification?: (data: CrosstabsClassificationData) => void
  onCrosstabsObjectives?: (data: CrosstabsObjectivesData) => void
  onCrosstabsPlan?: (data: CrosstabsPlanData) => void
  onCrosstabsPanelOpen?: (tab: CrosstabsTab) => void
  onMcpApp?: (data: McpAppData) => void
  apiBaseUrl?: string
  sessionId?: string
  devMode?: boolean
  onApprovePlan?: (sessionId: string) => Promise<void>
  uploadFile?: (sessionId: string, file: File) => Promise<string>
  fetchVoiceToken?: () => Promise<string>
}

const R3Chat = forwardRef<R3ChatHandle, R3ChatProps>(({chatService, onSessionCreated, reachyAvatarUrl, headerActions, onMessagesChange, onSessionInfoChange, onSiteBuilderPlan: onSiteBuilderPlanProp, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onStreamingAssistantContent, onCrosstabsClassification: onCrosstabsClassificationProp, onCrosstabsObjectives: onCrosstabsObjectivesProp, onCrosstabsPlan: onCrosstabsPlanProp, onCrosstabsPanelOpen, onMcpApp: onMcpAppProp, apiBaseUrl, sessionId: externalSessionId, devMode, onApprovePlan, uploadFile, fetchVoiceToken}, ref) => {
  const {trackEvent} = usePostHog()
  // Optional: present in Studio (drives department-tagged tracking/chat), absent in standalone Ros
  const department = useOptionalUserIdentity()?.department ?? null

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(externalSessionId)
  const [contextInfo, setContextInfo] = useState<{contextTokens: number; contextWindow: number; compacted: boolean} | undefined>()
  const [activeOptions, setActiveOptions] = useState<SiteBuilderOptionsData | null>(null)
  const [activeDiscoveryQuestion, setActiveDiscoveryQuestion] = useState<DiscoveryQuestionData | null>(null)
  const [pendingFileIds, setPendingFileIds] = useState<string[]>([])
  const [pendingFiles, setPendingFiles] = useState<{name: string; id?: string; uploading: boolean}[]>([])
  const streamingContentRef = useRef('')
  const streamingMsgIdRef = useRef<string | null>(null)
  const [activeAgent, setActiveAgent] = useState<string | undefined>(undefined)
  const [pendingMcpAppInput, setPendingMcpAppInput] = useState<McpAppInputData | null>(null)
  // mcpModelContextRef removed — two-step context deferral now handled inside McpAppContainer

  const {state: crosstabsState, callbacks: crosstabsCallbacks, dismissOptions: dismissCrosstabsOptions, dismissObjectives: dismissCrosstabsObjectives, dismissFileRequest: dismissCrosstabsFileRequest, clearProgress: clearCrosstabsProgress, reset: resetCrosstabs} = useCrosstabsState(onCrosstabsPanelOpen)

  // Voice input — on unexpected WS close, preserve transcribed text in input
  const setInputValueRef = useRef(setInputValue)
  setInputValueRef.current = setInputValue
  const voiceOnTranscribed = useCallback((text: string) => {
    setInputValueRef.current(text)
  }, [])
  const voiceOnError = useCallback((message: string) => {
    console.error('[R3Chat] Voice input error:', message)
  }, [])
  const noopFetchToken = useCallback(async () => { throw new Error('Voice input not configured') }, [])
  const {status: voiceStatus, transcript: voiceTranscript, toggle: toggleVoice} = useVoiceInput({
    fetchToken: fetchVoiceToken ?? noopFetchToken,
    onTranscribed: voiceOnTranscribed,
    onError: voiceOnError
  })
  const handleMicToggle = useCallback(() => {
    trackEvent('r3_chat_voice_toggled', {
      user_department: department,
    })
    const text = toggleVoice()
    if (text) {
      setInputValue(text)
    }
  }, [toggleVoice])

  useImperativeHandle(ref, () => ({
    reset: () => {
      chatService.cancel()
      streamingMsgIdRef.current = null
      setActiveAgent(undefined)
      setMessages([])
      setSessionId(undefined)
      setInputValue('')
      setIsStreaming(false)
      setContextInfo(undefined)
      setActiveOptions(null)
      setActiveDiscoveryQuestion(null)
      setPendingFileIds([])
      setPendingFiles([])
      resetCrosstabs()
      onMessagesChange?.([])
      onSessionInfoChange?.({})
    },
    sendMessage: (text: string) => {
      handleSend(text)
    },
    loadSession: (newSessionId: string, history: Array<{role: 'user' | 'assistant'; content: string; agent?: string; handoff?: {from: string; to: string}}>) => {
      chatService.cancel()
      streamingMsgIdRef.current = null
      setIsStreaming(false)
      setContextInfo(undefined)
      setActiveOptions(null)
      setActiveDiscoveryQuestion(null)
      setPendingFileIds([])
      setPendingFiles([])
      resetCrosstabs()

      // Set the session ID so subsequent messages go to this session
      setSessionId(newSessionId)

      // Load history as chat messages — restore agent + handoff for mascot/card display
      const chatMessages: ChatMessage[] = history
        .filter(m => m.content?.trim() || m.handoff)
        .map(m => ({
          id: uuidv4(),
          role: m.role,
          content: m.content || '',
          ...(m.agent ? { intent: { intent: m.agent, confidence: 1, reasoning: 'restored' } } : {}),
          ...(m.handoff ? { handoff: m.handoff } : {}),
        }))
      setMessages(chatMessages)
      onMessagesChange?.(chatMessages.map(m => ({ role: m.role, content: m.content })))
      onSessionInfoChange?.({ intentSummary: 'Resumed session' })
    }
  }))

  // Mirror messages to parent for export
  useEffect(() => {
    if (!onMessagesChange) return
    onMessagesChange(
      messages
        .filter(m => !m.isStreaming)
        .map(m => ({role: m.role, content: m.content}))
    )
  }, [messages, onMessagesChange])

  const handleStop = useCallback(() => {
    chatService.cancel()
    const msgId = streamingMsgIdRef.current
    if (msgId) {
      setMessages(prev =>
        prev.map(m =>
          m.id === msgId
            ? {...m, content: m.content + ' *(stopped)*', isStreaming: false, aborted: true}
            : m
        )
      )
      streamingMsgIdRef.current = null
    }
    streamingContentRef.current = ''
    onStreamingAssistantContent?.(null)
    setIsStreaming(false)
  }, [chatService, onStreamingAssistantContent])

  const handleSend = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? inputValue).trim()
    const hasFiles = pendingFileIds.length > 0
    if ((!text && !hasFiles) || isStreaming) return

    // Ensure a session ID exists (CF backend needs it for DO routing)
    const sid = sessionId ?? uuidv4()
    if (!sessionId) {
      setSessionId(sid)
      trackEvent('r3_chat_created', {
        user_department: department,
      })
    }

    trackEvent('r3_chat_message_sent', {
      session_id: sid,
      user_department: department,
    })

    setInputValue('')
    setActiveOptions(null) // Dismiss options widget on send
    setActiveDiscoveryQuestion(null) // Dismiss discovery question widget on send
    dismissCrosstabsOptions()
    dismissCrosstabsObjectives()
    dismissCrosstabsFileRequest()

    // Build display text — include file names if sending files without text
    const fileNames = pendingFiles.filter(f => f.id).map(f => f.name)
    const displayText = text || `[Attached ${fileNames.length} file${fileNames.length > 1 ? 's' : ''}: ${fileNames.join(', ')}]`
    const messageText = text || `I've attached these files: ${fileNames.join(', ')}`

    // Add user message
    const userMsg: ChatMessage = {id: uuidv4(), role: 'user', content: displayText}
    setMessages(prev => [...prev, userMsg])
    setIsStreaming(true)

    // Placeholder for streaming assistant message
    const assistantMsgId = uuidv4()
    streamingMsgIdRef.current = assistantMsgId
    setMessages(prev => [
      ...prev,
      {id: assistantMsgId, role: 'assistant', content: '', isStreaming: true}
    ])

    // Build history for context (exclude the placeholder we just added)
    const history = messages.map(m => ({role: m.role, content: m.content}))

    // Collect and clear pending file IDs
    const fileIds = [...pendingFileIds]
    setPendingFileIds([])
    setPendingFiles([])

    let currentIntent: IntentResult | undefined
    // Accumulate site builder event data in closure vars — merged in onComplete
    // to avoid React batching race (events arrive in same microtask as complete)
    let pendingPlan: SiteBuilderPlanData | undefined
    let pendingOptions: SiteBuilderOptionsData | undefined
    let pendingPreview: {siteId: number} | undefined
    let pendingPublished: {url: string; siteId: number} | undefined
    let pendingDiscoveryQuestion: DiscoveryQuestionData | undefined
    let pendingCrosstabsOptions: CrosstabsOptionsData | undefined
    let pendingCrosstabsObjectives: CrosstabsObjectivesData | undefined
    let pendingCrosstabsFileRequest: CrosstabsFileRequestData | undefined
    let pendingMcpApp: McpAppData | undefined
    let pendingHandoff: {from: string; to: string} | undefined
    let pendingProgressSteps: import('./types').ProgressStepData[] = []

    try {
      await chatService.sendMessage({
        message: messageText,
        sessionId: sid,
        department: department || undefined,
        history,
        attachedFileIds: fileIds.length > 0 ? fileIds : undefined,
        onChunk: (chunk) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantMsgId
                ? {...m, content: m.content + chunk, isStreaming: true}
                : m
            )
          )
          streamingContentRef.current += chunk
          onStreamingAssistantContent?.(streamingContentRef.current)
        },
        onIntent: (intent) => {
          currentIntent = intent
          setActiveAgent(intent.intent)
          onSessionInfoChange?.({intentSummary: intent.intent, startedAt: Date.now()})
          // Update streaming message immediately so the avatar switches right away
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, intent} : m)
          )
        },
        onSiteBuilderOptions: (data) => {
          pendingOptions = data
          setActiveOptions(data)
        },
        onSiteBuilderPlan: (data) => {
          pendingPlan = data
          onSiteBuilderPlanProp?.(data)
        },
        onSiteBuilderGenerating: (siteId) => {
          onSiteBuilderGenerating?.(siteId)
        },
        onSiteBuilderCodeChunk: (data) => {
          onSiteBuilderCodeChunk?.(data)
        },
        onSiteBuilderPreview: (html, siteId) => {
          pendingPreview = {siteId}
          onSiteBuilderPreview?.(html, siteId)
        },
        onSiteBuilderPublished: (url, siteId) => {
          pendingPublished = {url, siteId}
          onSiteBuilderPublished?.(url, siteId)
        },
        onDiscoveryQuestion: (data) => {
          pendingDiscoveryQuestion = data
          setActiveDiscoveryQuestion(data)
        },
        onCrosstabsProgress: crosstabsCallbacks.onCrosstabsProgress,
        onCrosstabsClassification: (data) => {
          crosstabsCallbacks.onCrosstabsClassification(data)
          onCrosstabsClassificationProp?.(data)
        },
        onCrosstabsObjectives: (data) => {
          pendingCrosstabsObjectives = data
          crosstabsCallbacks.onCrosstabsObjectives(data)
          onCrosstabsObjectivesProp?.(data)
        },
        onCrosstabsPlan: (data) => {
          crosstabsCallbacks.onCrosstabsPlan(data)
          onCrosstabsPlanProp?.(data)
        },
        onCrosstabsOptions: (data) => {
          pendingCrosstabsOptions = data
          crosstabsCallbacks.onCrosstabsOptions(data)
        },
        onCrosstabsFileRequest: (data) => {
          pendingCrosstabsFileRequest = data
          crosstabsCallbacks.onCrosstabsFileRequest(data)
        },
        onAuthoringStep: (data) => {
          // Upsert: replace existing step with same stepId, or append
          const idx = pendingProgressSteps.findIndex(s => s.stepId === data.stepId)
          if (idx >= 0) pendingProgressSteps[idx] = data
          else pendingProgressSteps.push(data)
          // Apply immediately so steps render live during streaming
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
          )
        },
        onAuthoringIteration: (data) => {
          // Convert iteration event to a progress step
          const step: import('./types').ProgressStepData = {
            stepId: 'generation_iteration',
            status: data.issueCount > 0 ? 'warning' : 'running',
            name: `Generation — iteration ${data.iteration}/${data.maxIterations}`,
            ...(data.issueCount > 0 && {
              description: `${data.issueCount} issue${data.issueCount > 1 ? 's' : ''} found`,
              substeps: data.issues.map((issue, i) => ({name: issue, status: 'warning', result: ''})),
            }),
          }
          const idx = pendingProgressSteps.findIndex(s => s.stepId === 'generation_iteration')
          if (idx >= 0) pendingProgressSteps[idx] = step
          else pendingProgressSteps.push(step)
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
          )
        },
        onAuthoringVerify: (data) => {
          // Convert verification event to a progress step with substeps per pass
          const step: import('./types').ProgressStepData = {
            stepId: `verify_${data.type}`,
            status: data.passed ? 'complete' : 'warning',
            name: data.type === 'semantic' ? 'Quality Checks' : data.type === 'runtime' ? 'Test Run' : `Verification: ${data.type}`,
            substeps: [
              ...(data.passes ?? []).map(p => ({
                name: p.name,
                status: p.passed ? 'complete' : 'warning',
              })),
              ...(data.tracks ?? []).map(t => ({
                name: t.name,
                status: t.passed ? 'complete' : 'warning',
                result: `${t.cardCount} cards → ${t.terminal}`,
              })),
            ],
          }
          const idx = pendingProgressSteps.findIndex(s => s.stepId === step.stepId)
          if (idx >= 0) pendingProgressSteps[idx] = step
          else pendingProgressSteps.push(step)
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
          )
        },
        onAuthoringStepUpdate: (data) => {
          const step = pendingProgressSteps.find(s => s.stepId === data.stepId)
          if (step) {
            if (data.status) step.status = data.status as typeof step.status
            if (data.timing) step.timing = data.timing
            if (data.details) step.details = data.details
            if (data.substeps) step.substeps = data.substeps
            setMessages(prev =>
              prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
            )
          }
        },
        onMcpApp: (data) => {
          pendingMcpApp = data
          onMcpAppProp?.(data)
        },
        onMcpAppInput: (data) => {
          setPendingMcpAppInput(data)
        },
        onHandoff: (data) => {
          pendingHandoff = {from: data.from, to: data.to ?? 'general'}
          setActiveAgent(data.to === 'general' ? undefined : data.to)
          // Apply handoff immediately so the card renders during streaming
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, handoff: pendingHandoff} : m)
          )
        },
        onComplete: (newSessionId, _tokenUsage, info) => {
          streamingMsgIdRef.current = null
          streamingContentRef.current = ''
          clearCrosstabsProgress() // drop any "Computing… (Ns)" line now the turn ended
          onStreamingAssistantContent?.(null)
          const isFirstMessage = !sessionId
          setSessionId(newSessionId)
          if (isFirstMessage && onSessionCreated) {
            onSessionCreated(newSessionId)
          }
          if (info) setContextInfo(info)
          // Single setMessages call merges all accumulated event data
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    isStreaming: false,
                    intent: currentIntent,
                    ...(pendingPlan && {siteBuilderPlan: pendingPlan}),
                    ...(pendingOptions && {siteBuilderOptions: pendingOptions}),
                    ...(pendingPreview && {siteBuilderPreview: pendingPreview}),
                    ...(pendingPublished && {siteBuilderPublished: pendingPublished}),
                    ...(pendingDiscoveryQuestion && {discoveryQuestion: pendingDiscoveryQuestion}),
                    ...(pendingCrosstabsOptions && {crosstabsOptions: pendingCrosstabsOptions}),
                    ...(pendingCrosstabsObjectives && {crosstabsObjectives: pendingCrosstabsObjectives}),
                    ...(pendingCrosstabsFileRequest && {crosstabsFileRequest: pendingCrosstabsFileRequest}),
                    ...(pendingMcpApp && {mcpApp: pendingMcpApp}),
                    ...(pendingHandoff && {handoff: pendingHandoff}),
                    ...(pendingProgressSteps.length > 0 && {progressSteps: pendingProgressSteps})
                  }
                : m
            )
          )
          setIsStreaming(false)
        },
        onError: (err) => {
          streamingMsgIdRef.current = null
          streamingContentRef.current = ''
          onStreamingAssistantContent?.(null)
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    content: `Sorry, something went wrong: ${err.message}`,
                    isStreaming: false
                  }
                : m
            )
          )
          setIsStreaming(false)
        }
      })
    } catch (err) {
      streamingMsgIdRef.current = null
      // If this was an abort, the handleStop already finalized the message
      if ((err as Error)?.name === 'AbortError') return
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: 'Sorry, something went wrong. Please try again.',
                isStreaming: false
              }
            : m
        )
      )
      setIsStreaming(false)
    }
  }, [inputValue, isStreaming, messages, sessionId, chatService, onSessionCreated, pendingFileIds, onSiteBuilderPlanProp, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onStreamingAssistantContent, onSessionInfoChange, crosstabsCallbacks, onCrosstabsClassificationProp, onCrosstabsObjectivesProp, onCrosstabsPlanProp, dismissCrosstabsOptions, dismissCrosstabsObjectives, dismissCrosstabsFileRequest])



  const handleOptionsSubmit = useCallback((formattedMessage: string) => {
    handleSend(formattedMessage)
  }, [handleSend])

  const handleFileAttach = useCallback(async (files: File[]) => {
    if (!uploadFile) return
    trackEvent('r3_chat_document_uploaded', {
      file_count: files.length,
      user_department: department,
    })
    const sid = sessionId ?? uuidv4()
    if (!sessionId) setSessionId(sid)

    const startIdx = pendingFiles.length
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const idx = startIdx + i
      setPendingFiles(prev => [...prev, {name: file.name, uploading: true}])

      try {
        const fileId = await uploadFile(sid, file)
        setPendingFileIds(prev => [...prev, fileId])
        setPendingFiles(prev =>
          prev.map((f, j) => j === idx ? {...f, id: fileId, uploading: false} : f)
        )
      } catch (err) {
        console.error('File upload failed:', err)
        trackEvent('r3_chat_error', {
          error_type: 'file_upload_failed',
          error_message: err instanceof Error ? err.message : 'File upload failed',
          user_department: department,
        })
        setPendingFiles(prev =>
          prev.map((f, j) => j === idx ? {...f, uploading: false} : f)
        )
      }
    }
  }, [uploadFile, sessionId, pendingFiles.length])

  const handleApprovePlan = useCallback(async (message: string) => {
    // Set planApproved in the DO before sending the message to the LLM
    if (onApprovePlan && sessionId) {
      try {
        await onApprovePlan(sessionId)
      } catch (err) {
        console.error('Failed to approve plan in DO:', err)
      }
    }
    handleSend(message)
  }, [onApprovePlan, sessionId, handleSend])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
          minHeight: 44
        }}
      >
        {headerActions}
      </Box>

      {/* Context window status — only visible after first message */}
      {contextInfo && (
        <ContextWindowBar
          contextTokens={contextInfo.contextTokens}
          contextWindow={contextInfo.contextWindow}
          compacted={contextInfo.compacted}
        />
      )}

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        reachyAvatarUrl={reachyAvatarUrl}
        activeAgent={activeAgent}
        devMode={devMode}
        onSend={handleSend}
        onApprovePlan={handleApprovePlan}
        progressMessage={crosstabsState.progress ? `${crosstabsState.progress.message} (${crosstabsState.progress.elapsedSec}s)` : null}
        apiBaseUrl={apiBaseUrl}
        sessionId={sessionId}
        mcpPanelMode={!!onMcpAppProp}
        onMcpMessage={(text) => handleSend(text)}
        pendingMcpAppInput={pendingMcpAppInput}
        onMcpAppInputConsumed={() => setPendingMcpAppInput(null)}
      />

      {/* Site Builder Options Widget — floats above input when active */}
      {activeOptions && (
        <SiteBuilderOptionsWidget
          data={activeOptions}
          onSubmit={handleOptionsSubmit}
        />
      )}

      {/* Discovery Question Widget — floats above input when active */}
      {activeDiscoveryQuestion && (
        <DiscoveryQuestionWidget
          data={activeDiscoveryQuestion}
          onSubmit={handleOptionsSubmit}
        />
      )}

      {/* Crosstabs Options Widget — same shape as SiteBuilderOptionsWidget */}
      {crosstabsState.activeOptions && (
        <SiteBuilderOptionsWidget
          data={{question: crosstabsState.activeOptions.question, type: crosstabsState.activeOptions.type, options: (crosstabsState.activeOptions.options ?? []).map(o => ({id: o.id, label: o.label, description: o.description}))}}
          onSubmit={handleOptionsSubmit}
        />
      )}

      {/* Crosstabs Objectives Widget — multi-select checklist */}
      {crosstabsState.activeObjectives && (
        <ObjectivesWidget
          data={crosstabsState.activeObjectives}
          onSubmit={handleOptionsSubmit}
        />
      )}

      {/* Input */}
      <MessageInput
        value={inputValue}
        onChange={setInputValue}
        onSend={() => handleSend()}
        onStop={handleStop}
        disabled={isStreaming}
        isStreaming={isStreaming}
        onAttach={uploadFile ? handleFileAttach : undefined}
        onMic={fetchVoiceToken ? handleMicToggle : undefined}
        isRecording={voiceStatus === 'recording'}
        isVoiceConnecting={voiceStatus === 'connecting'}
        voiceTranscript={voiceTranscript}
        pendingFiles={pendingFiles}
      />
    </Box>
  )
})

R3Chat.displayName = 'R3Chat'

export default R3Chat
